import { useState } from 'react';
import { Button } from '@/components/Button';
import { ASSISTANCE_TYPES } from './api';
import type { AssistanceType } from './api';
import { useCustomer } from './context';
import { Notice, PageTitle, Panel, formatTime } from './parts';
import { useAssistanceMemory, useRequestAssistance } from './queries';
import { ASSISTANCE_LABEL, describeCustomerError } from './wording';

/**
 * Ask for assistance. The backend keeps one active request per kind and returns it when asked again,
 * so asking twice never creates a duplicate. It offers guests no way to read a request's progress, so
 * this screen shows only what the server answered: sent, or already active. It never claims "resolved".
 */
export function HelpPage() {
  const { session } = useCustomer();
  const memory = useAssistanceMemory();
  const request = useRequestAssistance();
  const [note, setNote] = useState('');
  const pendingType = request.isPending ? request.variables.type : null;
  const failure = request.isError
    ? describeCustomerError(request.error, session.locationType)
    : null;

  function ask(type: AssistanceType) {
    const trimmed = note.trim();
    request.mutate({ type, note: type === 'OTHER' && trimmed ? trimmed : undefined });
  }

  return (
    <>
      <PageTitle>Help</PageTitle>
      <p className="mb-4 text-sm text-muted">
        Tell staff what you need. They are notified straight away.
      </p>
      {failure && (
        <div className="mb-4">
          <Notice tone="error">
            <strong className="block">{failure.title}</strong>
            {failure.message}
          </Notice>
        </div>
      )}
      <Panel>
        <ul className="divide-y divide-line">
          {ASSISTANCE_TYPES.map((type) => {
            const words = ASSISTANCE_LABEL[type];
            const outcome = memory[type];
            const requesting = pendingType === type;
            return (
              <li key={type} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-ink">{words.title}</h2>
                    <p className="text-sm text-muted">{words.hint}</p>
                  </div>
                  <Button
                    variant={outcome ? 'secondary' : 'primary'}
                    loading={requesting}
                    disabled={request.isPending && !requesting}
                    aria-label={outcome ? `Ask again: ${words.title}` : `Request: ${words.title}`}
                    onClick={() => ask(type)}
                  >
                    {requesting ? 'Requesting' : outcome ? 'Ask again' : 'Request'}
                  </Button>
                </div>
                {type === 'OTHER' && (
                  <div className="mt-3 flex flex-col gap-1.5">
                    <label htmlFor="help-note" className="text-sm font-medium text-ink">
                      What do you need? <span className="font-normal text-muted">(optional)</span>
                    </label>
                    <input
                      id="help-note"
                      value={note}
                      maxLength={255}
                      onChange={(event) => setNote(event.target.value)}
                      className="min-h-12 w-full rounded-control border border-field bg-white px-3.5 text-base text-ink"
                    />
                  </div>
                )}
                {outcome && !requesting && (
                  <p
                    role="status"
                    className="mt-3 rounded-control bg-success-soft px-3 py-2 text-sm font-medium text-success"
                  >
                    {outcome.created
                      ? `Request sent at ${formatTime(outcome.request.created_at)}. Staff have been notified.`
                      : `Already requested at ${formatTime(outcome.request.created_at)}. Staff have been notified.`}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </Panel>
    </>
  );
}
